import { useState, useEffect, useRef, useMemo } from "react";
import {
  Plus, Pencil, Trash2, X, ChevronDown, FolderKanban, Calendar,
  TrendingUp, Wallet, Receipt, Users2, GanttChartSquare, LayoutGrid, ChevronLeft, ChevronRight, FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { projectsApi, staffApi } from "@/lib/unifiedApi";
import { toast } from "sonner";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, isToday, isSameMonth, differenceInCalendarDays } from "date-fns";

// ── Tax constants (company IS VAT-registered → input VAT is recoverable) ──
const VAT_RATE = 0.07;
const WHT_OPTIONS = [
  { value: 0,  label: "ไม่หัก" },
  { value: 1,  label: "1% · ขนส่ง" },
  { value: 2,  label: "2% · โฆษณา" },
  { value: 3,  label: "3% · บริการ / จ้างทำของ" },
  { value: 5,  label: "5% · ค่าเช่า" },
  { value: 10, label: "10% · วิชาชีพ" },
];

const STATUS = {
  planning:  { label: "วางแผน",  color: "#64748b", bg: "#f1f5f9" },
  active:    { label: "กำลังทำ", color: "#16a34a", bg: "#dcfce7" },
  done:      { label: "เสร็จ",   color: "#3b6ef5", bg: "#e6edfd" },
  cancelled: { label: "ยกเลิก",  color: "#dc2626", bg: "#fee2e2" },
};
const STATUS_ORDER = ["planning", "active", "done", "cancelled"];

const AVATAR_COLORS = ["#3b6ef5", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4"];
const avatarColor = (name) => AVATAR_COLORS[(name || "").charCodeAt(0) % AVATAR_COLORS.length] || "#3b6ef5";

// ── Money helpers ──
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const getAssignees = (p) => {
  if (!p?.assigned_to) return [];
  if (Array.isArray(p.assigned_to)) return p.assigned_to.filter(Boolean);
  return [p.assigned_to];
};

// Per-item and per-project cost calculation
function calcItem(item) {
  const base = Number(item.base) || 0;             // ประมาณ (estimate, ex-VAT)
  const actual = Number(item.actual) || 0;         // จ่ายจริง (ex-VAT); 0 = ยังไม่จ่าย/ยังไม่กรอก
  const effective = actual > 0 ? actual : base;    // ใช้จริงถ้ามี ไม่งั้นใช้ประมาณ
  const variance = actual > 0 ? round2(actual - base) : 0; // +เกินงบ / −ต่ำกว่างบ (เฉพาะที่จ่ายแล้ว)
  const vatAmount = item.vat ? round2(effective * VAT_RATE) : 0;
  const whtRate = Number(item.whtRate) || 0;
  const whtAmount = round2((effective * whtRate) / 100);
  const incVat = round2(effective + vatAmount);
  const payable = round2(incVat - whtAmount);
  return { ...item, base, actual, effective, variance, vatAmount, whtRate, whtAmount, incVat, payable };
}
// Income installments — fall back to the legacy single `revenue` number for
// projects saved before installments existed.
function projectIncomes(p) {
  if (p.incomes && p.incomes.length) return p.incomes;
  const r = Number(p.revenue) || 0;
  return r > 0 ? [{ id: "legacy", label: "รายรับ", amount: r, dueDate: null, paid: false, paidDate: null }] : [];
}
function calcProject(p) {
  const items = (p.items || []).map(calcItem);
  const totalBase = round2(items.reduce((s, i) => s + i.base, 0));       // ประมาณรวม
  const totalActual = round2(items.reduce((s, i) => s + i.actual, 0));   // จ่ายจริงรวม (เฉพาะที่กรอก)
  const totalEffective = round2(items.reduce((s, i) => s + i.effective, 0));
  const costVariance = round2(items.reduce((s, i) => s + i.variance, 0)); // เกิน/ต่ำกว่างบ (เฉพาะที่จ่ายแล้ว)
  const hasActual = items.some((i) => i.actual > 0);
  const totalVat = round2(items.reduce((s, i) => s + i.vatAmount, 0));
  const totalWht = round2(items.reduce((s, i) => s + i.whtAmount, 0));
  const totalIncVat = round2(items.reduce((s, i) => s + i.incVat, 0));
  const totalPayable = round2(items.reduce((s, i) => s + i.payable, 0));
  const incomes = projectIncomes(p);
  const revenue = round2(incomes.reduce((s, i) => s + (Number(i.amount) || 0), 0));
  const received = round2(incomes.filter((i) => i.paid).reduce((s, i) => s + (Number(i.amount) || 0), 0));
  const outstanding = round2(revenue - received);
  const estCost = totalBase;
  const actualCost = totalActual;
  const cost = totalEffective; // VAT-registered → ex-VAT; ใช้จริงถ้ามี ไม่งั้นใช้ประมาณ
  const profit = round2(revenue - cost);
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
  return { items, totalBase, totalActual, totalEffective, costVariance, hasActual, totalVat, totalWht, totalIncVat, totalPayable, incomes, revenue, received, outstanding, estCost, actualCost, cost, profit, margin };
}

const dateRangeLabel = (s, e) => {
  if (!s && !e) return "ยังไม่กำหนดวันที่";
  try {
    if (s && e && s !== e) return `${format(parseISO(s), "d MMM")} – ${format(parseISO(e), "d MMM yyyy")}`;
    return format(parseISO(s || e), "d MMM yyyy");
  } catch { return "-"; }
};

// Printed documents keep Thai Buddhist-era dates (official paperwork).
const thDate = (iso) => { if (!iso) return "-"; try { return new Date(iso).toLocaleDateString("th-TH", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };

// Export a project as a printable PDF: kind "quotation" (client-facing price)
// or "cost" (internal cost/profit sheet). Opens a print window like the voucher.
function openProjectPrint(project, kind) {
  const calc = calcProject(project);
  const esc = (s) => String(s || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const isQuote = kind === "quotation";
  const title = isQuote ? "ใบเสนอราคา" : "สรุปต้นทุน & กำไร";
  const titleEn = isQuote ? "Quotation" : "Cost Summary (Internal)";

  const bodyRows = isQuote
    ? (calc.incomes.length ? calc.incomes : [{ label: "รายรับ", amount: calc.revenue }]).map((inc, i) => `
        <tr><td>${i + 1}</td><td>${esc(inc.label) || "งวด"}</td>
        <td class="c">${inc.dueDate ? thDate(inc.dueDate) : "-"}</td>
        <td class="c">${inc.paid ? '<span class="ok">รับแล้ว</span>' : '<span class="due">รอชำระ</span>'}</td>
        <td class="r">฿${fmt(inc.amount)}</td></tr>`).join("")
    : calc.items.map((it, i) => `
        <tr><td>${i + 1}</td><td>${esc(it.description) || "(ไม่ระบุ)"}</td>
        <td class="r">฿${fmt(it.base)}</td>
        <td class="r">${it.actual > 0 ? "฿" + fmt(it.actual) : "-"}</td>
        <td class="r ${it.variance > 0 ? "neg" : it.variance < 0 ? "pos" : ""}">${it.actual > 0 ? (it.variance > 0 ? "+" : "") + fmt(it.variance) : "-"}</td></tr>`).join("");

  const head = isQuote
    ? `<tr><th style="width:26px">#</th><th>งวด / รายการ</th><th class="c" style="width:110px">กำหนดชำระ</th><th class="c" style="width:80px">สถานะ</th><th class="r" style="width:110px">จำนวนเงิน</th></tr>`
    : `<tr><th style="width:26px">#</th><th>รายการต้นทุน</th><th class="r" style="width:100px">ประมาณ</th><th class="r" style="width:100px">จ่ายจริง</th><th class="r" style="width:100px">ส่วนต่าง</th></tr>`;

  const foot = isQuote
    ? `<tr class="tot"><td colspan="4" class="r">รวมเป็นเงิน</td><td class="r">฿${fmt(calc.revenue)}</td></tr>`
    : `<tr class="tot"><td colspan="2" class="r">รวม</td><td class="r">฿${fmt(calc.estCost)}</td><td class="r">${calc.hasActual ? "฿" + fmt(calc.actualCost) : "-"}</td><td class="r">${calc.hasActual ? (calc.costVariance > 0 ? "+" : "") + fmt(calc.costVariance) : "-"}</td></tr>`;

  const summary = isQuote
    ? `<div class="sum">
        <div class="srow"><span>รับชำระแล้ว</span><b class="pos">฿${fmt(calc.received)}</b></div>
        <div class="srow"><span>คงเหลือ</span><b>฿${fmt(calc.outstanding)}</b></div>
        <div class="srow big"><span>รวมทั้งสิ้น</span><b>฿${fmt(calc.revenue)}</b></div>
       </div>`
    : `<div class="sum">
        <div class="srow"><span>รายได้ (ราคาขาย)</span><b>฿${fmt(calc.revenue)}</b></div>
        <div class="srow"><span>ต้นทุน ${calc.hasActual ? "(จ่ายจริง)" : "(ประมาณ)"}</span><b>฿${fmt(calc.cost)}</b></div>
        <div class="srow"><span>VAT เคลมคืน · หัก ณ ที่จ่าย</span><b>฿${fmt(calc.totalVat)} · ฿${fmt(calc.totalWht)}</b></div>
        <div class="srow big"><span>กำไร · ${calc.margin.toFixed(1)}%</span><b class="${calc.profit >= 0 ? "pos" : "neg"}">฿${fmt(calc.profit)}</b></div>
       </div>`;

  const names = getAssignees(project);
  const html = `<!doctype html><html lang="th"><head><meta charset="utf-8"><title>${title} — ${esc(project.name)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Sarabun','Prompt',-apple-system,sans-serif;color:#1e293b;padding:28px 32px;font-size:13px;line-height:1.5}
  .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #3b6ef5;padding-bottom:12px;margin-bottom:16px}
  .co{font-size:20px;font-weight:800;color:#3b6ef5;letter-spacing:-.5px}
  .co small{display:block;font-size:10px;color:#94a3b8;font-weight:400;letter-spacing:0}
  .doc{text-align:right}.doc .t{font-size:18px;font-weight:800}.doc .e{font-size:10px;color:#94a3b8}
  .meta{display:flex;justify-content:space-between;gap:16px;margin-bottom:14px}
  .lbl{font-size:9px;color:#94a3b8;text-transform:uppercase;letter-spacing:.4px}
  .val{font-size:13px;font-weight:600}
  table{width:100%;border-collapse:collapse;margin-bottom:14px}
  th{background:#f1f5f9;color:#64748b;font-size:10px;text-transform:uppercase;text-align:left;padding:7px 9px;font-weight:700}
  td{padding:7px 9px;border-bottom:1px solid #f1f5f9;font-variant-numeric:tabular-nums}
  .r{text-align:right}.c{text-align:center}
  .tot td{background:#f8fafc;font-weight:800;border-top:2px solid #e2e8f0;border-bottom:none}
  .pos{color:#16a34a}.neg{color:#dc2626}
  .ok{color:#16a34a;font-weight:600}.due{color:#d97706;font-weight:600}
  .sum{margin-left:auto;width:280px}
  .srow{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #f1f5f9;font-size:12px;color:#64748b}
  .srow b{color:#1e293b;font-variant-numeric:tabular-nums}
  .srow.big{border-bottom:none;border-top:2px solid #e2e8f0;margin-top:2px;padding-top:8px;font-size:15px;font-weight:800;color:#1e293b}
  .note{margin-top:18px;font-size:10px;color:#94a3b8}
  .btn{margin-top:22px;padding:9px 18px;background:#3b6ef5;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
  @media print{.btn{display:none}body{padding:0}}
  @page{margin:14mm}
</style></head><body>
<div class="head">
  <div class="co">FLASHYOURMEME<small>FLASHYOURMEME Co., Ltd.</small></div>
  <div class="doc"><div class="t">${title}</div><div class="e">${titleEn}</div><div class="e" style="margin-top:4px">วันที่ ${thDate(new Date().toISOString())}</div></div>
</div>
<div class="meta">
  <div><div class="lbl">โปรเจกต์</div><div class="val">${esc(project.name)}</div>${project.description ? `<div style="font-size:11px;color:#64748b;margin-top:2px">${esc(project.description)}</div>` : ""}</div>
  <div style="text-align:right"><div class="lbl">ระยะเวลา</div><div class="val" style="font-size:12px">${project.start_date ? thDate(project.start_date) : "-"}${project.end_date && project.end_date !== project.start_date ? " – " + thDate(project.end_date) : ""}</div>${names.length ? `<div class="lbl" style="margin-top:6px">ผู้รับผิดชอบ</div><div style="font-size:11px">${names.map(esc).join(", ")}</div>` : ""}</div>
</div>
<table><thead>${head}</thead><tbody>${bodyRows || `<tr><td colspan="5" style="text-align:center;color:#94a3b8;padding:18px">ไม่มีรายการ</td></tr>`}${foot}</tbody></table>
${summary}
${isQuote ? '<div class="note">* ราคานี้ยังไม่รวม/รวมภาษีมูลค่าเพิ่มตามที่ตกลง · ใบเสนอราคานี้มีผล 30 วัน</div>' : '<div class="note">* เอกสารภายใน — สำหรับใช้บริหารต้นทุนเท่านั้น ไม่ใช่ใบเสนอราคา</div>'}
<button class="btn" onclick="window.print()">พิมพ์ / บันทึก PDF</button>
</body></html>`;
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
  else toast.error("เปิดหน้าต่างพิมพ์ไม่ได้ — อนุญาต popup ก่อน");
}

// ── Multi-staff picker (checkbox dropdown) ──
const MultiStaffSelect = ({ value = [], onChange, options = [] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const toggle = (name) =>
    onChange(value.includes(name) ? value.filter((n) => n !== name) : [...value, name]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3b6ef5]/30 focus:border-[#3b6ef5] min-h-[38px] transition-colors"
      >
        <div className="flex-1 min-w-0 flex flex-wrap gap-1">
          {value.length === 0 ? (
            <span className="text-gray-400">เลือกผู้รับผิดชอบ</span>
          ) : (
            value.map((name) => (
              <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                {name}
                <button type="button" onClick={(e) => { e.stopPropagation(); toggle(name); }} className="text-blue-400 hover:text-blue-700 leading-none">
                  <X size={10} />
                </button>
              </span>
            ))
          )}
        </div>
        <ChevronDown size={15} className="text-gray-400 flex-shrink-0 ml-2" />
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-52 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">ไม่มีพนักงาน</div>
          ) : (
            options.map((s) => {
              const checked = value.includes(s.name);
              return (
                <button key={s.id} type="button" onClick={() => toggle(s.name)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-100 text-sm text-left transition-colors">
                  <div className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0"
                    style={{ background: checked ? "#3b6ef5" : "white", borderColor: checked ? "#3b6ef5" : "#d1d5db" }}>
                    {checked && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className="text-gray-700">{s.name}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const AvatarStack = ({ names = [], size = 26 }) => {
  const shown = names.slice(0, 4);
  const extra = names.length - shown.length;
  return (
    <div className="flex items-center">
      {shown.map((n, i) => (
        <span key={n} title={n}
          className="rounded-full flex items-center justify-center text-white font-bold border-2 border-white"
          style={{ width: size, height: size, fontSize: size * 0.42, background: avatarColor(n), marginLeft: i === 0 ? 0 : -size * 0.42 }}>
          {n[0]}
        </span>
      ))}
      {extra > 0 && (
        <span className="rounded-full flex items-center justify-center text-gray-600 font-bold border-2 border-white bg-gray-200"
          style={{ width: size, height: size, fontSize: size * 0.38, marginLeft: -size * 0.42 }}>
          +{extra}
        </span>
      )}
    </div>
  );
};

const emptyItem = () => ({ id: Math.random().toString(36).slice(2), description: "", base: "", actual: "", vat: true, whtRate: 0 });
const emptyIncome = (label = "") => ({ id: Math.random().toString(36).slice(2), label, amount: "", dueDate: "", paid: false, paidDate: "" });
const EMPTY_FORM = {
  name: "", description: "", status: "planning",
  start_date: "", end_date: "", assigned_to: [],
  incomes: [emptyIncome("มัดจำ"), emptyIncome("งวดสุดท้าย")],
  items: [emptyItem()],
};

// ── Create / Edit dialog ──
function ProjectFormDialog({ open, onOpenChange, editing, staff, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name ?? "",
        description: editing.description ?? "",
        status: editing.status ?? "planning",
        start_date: editing.start_date ?? "",
        end_date: editing.end_date ?? "",
        assigned_to: getAssignees(editing),
        incomes: (editing.incomes?.length
          ? editing.incomes
          : (Number(editing.revenue) > 0 ? [{ label: "รายรับ", amount: editing.revenue, paid: false }] : [emptyIncome("มัดจำ"), emptyIncome("งวดสุดท้าย")])
        ).map((inc) => ({
          id: inc.id ?? Math.random().toString(36).slice(2),
          label: inc.label ?? "",
          amount: inc.amount != null ? String(inc.amount) : "",
          dueDate: inc.dueDate ?? "",
          paid: !!inc.paid,
          paidDate: inc.paidDate ?? "",
        })),
        items: (editing.items?.length ? editing.items : [emptyItem()]).map((it) => ({
          id: it.id ?? Math.random().toString(36).slice(2),
          description: it.description ?? "",
          base: it.base != null ? String(it.base) : "",
          actual: it.actual != null && it.actual !== 0 ? String(it.actual) : "",
          vat: it.vat ?? false,
          whtRate: Number(it.whtRate) || 0,
        })),
      });
    } else {
      setForm({ ...EMPTY_FORM, items: [emptyItem()] });
    }
  }, [open, editing]);

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (id, k, v) =>
    setForm((f) => ({ ...f, items: f.items.map((it) => (it.id === id ? { ...it, [k]: v } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (id) => setForm((f) => ({ ...f, items: f.items.filter((it) => it.id !== id) }));

  const setIncome = (id, k, v) =>
    setForm((f) => ({ ...f, incomes: f.incomes.map((inc) => {
      if (inc.id !== id) return inc;
      const next = { ...inc, [k]: v };
      if (k === "paid" && v && !next.paidDate) next.paidDate = new Date().toISOString().slice(0, 10);
      return next;
    }) }));
  const addIncome = () => setForm((f) => ({ ...f, incomes: [...f.incomes, emptyIncome()] }));
  const removeIncome = (id) => setForm((f) => ({ ...f, incomes: f.incomes.filter((inc) => inc.id !== id) }));

  const live = useMemo(() => calcProject(form), [form]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("กรุณาใส่ชื่อโปรเจค"); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("วันสิ้นสุดต้องไม่ก่อนวันเริ่ม"); return;
    }
    setSaving(true);
    try {
      const cleanItems = form.items
        .filter((it) => it.description.trim() || Number(it.base) > 0 || Number(it.actual) > 0)
        .map((it) => ({
          id: it.id, description: it.description.trim(),
          base: Number(it.base) || 0, actual: Number(it.actual) || 0, vat: !!it.vat, whtRate: Number(it.whtRate) || 0,
        }));
      const cleanIncomes = form.incomes
        .filter((inc) => inc.label.trim() || Number(inc.amount) > 0)
        .map((inc) => ({
          id: inc.id, label: inc.label.trim(), amount: Number(inc.amount) || 0,
          dueDate: inc.dueDate || null, paid: !!inc.paid, paidDate: inc.paid ? (inc.paidDate || null) : null,
        }));
      const revenueTotal = round2(cleanIncomes.reduce((s, inc) => s + inc.amount, 0));
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        status: form.status,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        assigned_to: form.assigned_to,
        incomes: cleanIncomes,
        revenue: revenueTotal, // kept in sync for portfolio totals / backward compat
        items: cleanItems,
      };
      if (editing) {
        await projectsApi.update(editing.id, payload);
        toast.success("บันทึกการแก้ไขแล้ว");
      } else {
        await projectsApi.create(payload);
        toast.success("สร้างโปรเจคแล้ว");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast.error("บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-900">{editing ? "แก้ไขโปรเจค" : "สร้างโปรเจคใหม่"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Basic info */}
          <div>
            <Label className="text-gray-700">ชื่อโปรเจค *</Label>
            <Input value={form.name} onChange={(e) => setField("name")(e.target.value)} placeholder="เช่น งาน Event เปิดตัวสินค้า ABC" className="mt-1" />
          </div>
          <div>
            <Label className="text-gray-700">รายละเอียด</Label>
            <Textarea value={form.description} onChange={(e) => setField("description")(e.target.value)} placeholder="รายละเอียดงาน ขอบเขต หมายเหตุ..." rows={2} className="mt-1" />
          </div>

          <div>
            <Label className="text-gray-700">สถานะ</Label>
            <Select value={form.status} onValueChange={setField("status")}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((k) => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-700">วันเริ่ม</Label>
              <Input value={form.start_date} onChange={(e) => setField("start_date")(e.target.value)} type="date" className="mt-1" />
            </div>
            <div>
              <Label className="text-gray-700">วันสิ้นสุด</Label>
              <Input value={form.end_date} onChange={(e) => setField("end_date")(e.target.value)} type="date" className="mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-gray-700">ผู้รับผิดชอบ</Label>
            <div className="mt-1">
              <MultiStaffSelect value={form.assigned_to} onChange={setField("assigned_to")} options={staff} />
            </div>
          </div>

          {/* Income installments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-gray-700">รายรับ (แบ่งงวด)</Label>
              <span className="text-xs text-gray-400">มัดจำ / งวดจบ · ติ๊ก “รับแล้ว” เมื่อได้เงิน</span>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="hidden sm:grid grid-cols-[1fr_120px_150px_92px_36px] gap-2 px-3 py-2 bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase">
                <div>งวด</div><div className="text-right">จำนวนเงิน</div><div>กำหนดชำระ</div><div className="text-center">รับแล้ว</div><div />
              </div>
              {form.incomes.map((inc) => (
                <div key={inc.id} className="grid grid-cols-2 sm:grid-cols-[1fr_120px_150px_92px_36px] gap-2 px-3 py-2 border-t border-gray-100 items-center">
                  <Input value={inc.label} onChange={(e) => setIncome(inc.id, "label", e.target.value)} placeholder="เช่น มัดจำ 50%" className="h-9 col-span-2 sm:col-span-1" />
                  <Input value={inc.amount} onChange={(e) => setIncome(inc.id, "amount", e.target.value)} type="number" step="0.01" placeholder="0.00" className="h-9 text-right" />
                  <Input value={inc.dueDate} onChange={(e) => setIncome(inc.id, "dueDate", e.target.value)} type="date" className="h-9" />
                  <label className="flex items-center justify-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={inc.paid} onChange={(e) => setIncome(inc.id, "paid", e.target.checked)} className="w-4 h-4 accent-emerald-600 cursor-pointer" />
                    <span className={`text-[11px] font-medium ${inc.paid ? "text-emerald-600" : "text-gray-400"}`}>{inc.paid ? "รับแล้ว" : "ค้าง"}</span>
                  </label>
                  <button type="button" onClick={() => removeIncome(inc.id)} className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 justify-self-center">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addIncome} className="mt-2 w-full py-2 rounded-lg border border-dashed border-gray-300 text-gray-500 text-sm font-medium hover:border-[#3b6ef5] hover:text-[#3b6ef5] transition-colors flex items-center justify-center gap-1.5">
              <Plus size={15} /> เพิ่มงวด
            </button>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 px-1 text-[12px]">
              <span className="text-gray-500">รายได้รวม <b className="text-[#3b6ef5]">฿{fmt(live.revenue)}</b></span>
              <span className="text-gray-500">รับแล้ว <b className="text-emerald-600">฿{fmt(live.received)}</b></span>
              <span className="text-gray-500">ค้างรับ <b className={live.outstanding > 0 ? "text-amber-600" : "text-gray-400"}>฿{fmt(live.outstanding)}</b></span>
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-gray-700">รายการค่าใช้จ่าย</Label>
              <span className="text-xs text-gray-400">ใส่ราคา “ก่อน VAT” (ฐานภาษี)</span>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="hidden sm:grid grid-cols-[1fr_100px_100px_54px_140px_36px] gap-2 px-3 py-2 bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase">
                <div>รายการ</div><div className="text-right">ประมาณ</div><div className="text-right">จ่ายจริง</div><div className="text-center">VAT</div><div>หัก ณ ที่จ่าย</div><div />
              </div>
              {form.items.map((it) => {
                const variance = Number(it.actual) > 0 ? round2(Number(it.actual) - (Number(it.base) || 0)) : 0;
                return (
                <div key={it.id} className="grid grid-cols-2 sm:grid-cols-[1fr_100px_100px_54px_140px_36px] gap-2 px-3 py-2 border-t border-gray-100 items-center">
                  <Input value={it.description} onChange={(e) => setItem(it.id, "description", e.target.value)} placeholder="เช่น เช่าสถานที่" className="h-9 col-span-2 sm:col-span-1" />
                  <Input value={it.base} onChange={(e) => setItem(it.id, "base", e.target.value)} type="number" step="0.01" placeholder="ประมาณ" className="h-9 text-right" />
                  <div className="relative">
                    <Input value={it.actual} onChange={(e) => setItem(it.id, "actual", e.target.value)} type="number" step="0.01" placeholder="จ่ายจริง" className="h-9 text-right" />
                    {variance !== 0 && <span className={`hidden sm:block absolute -bottom-3.5 right-0 text-[9px] font-semibold ${variance > 0 ? "text-red-500" : "text-emerald-600"}`}>{variance > 0 ? "+" : ""}{fmt(variance)}</span>}
                  </div>
                  <div className="flex justify-center">
                    <input type="checkbox" checked={it.vat} onChange={(e) => setItem(it.id, "vat", e.target.checked)} className="w-4 h-4 accent-[#3b6ef5] cursor-pointer" title="มี VAT 7%" />
                  </div>
                  <select value={it.whtRate} onChange={(e) => setItem(it.id, "whtRate", Number(e.target.value))}
                    className="h-9 px-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3b6ef5]/30">
                    {WHT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button type="button" onClick={() => removeItem(it.id)} className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 justify-self-center">
                    <Trash2 size={15} />
                  </button>
                </div>
                );
              })}
            </div>
            <button type="button" onClick={addItem} className="mt-2 w-full py-2 rounded-lg border border-dashed border-gray-300 text-gray-500 text-sm font-medium hover:border-[#3b6ef5] hover:text-[#3b6ef5] transition-colors flex items-center justify-center gap-1.5">
              <Plus size={15} /> เพิ่มรายการ
            </button>
          </div>

          {/* Live summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 rounded-xl bg-gray-50 p-3">
            <div>
              <p className="text-[11px] text-gray-500">ต้นทุน {live.hasActual ? "(จ่ายจริง)" : "(ประมาณ)"}</p>
              <p className="text-base font-bold text-gray-900">฿{fmt(live.cost)}</p>
              {live.hasActual && live.costVariance !== 0 && (
                <p className={`text-[10px] font-medium ${live.costVariance > 0 ? "text-red-500" : "text-emerald-600"}`}>{live.costVariance > 0 ? "เกินงบ" : "ต่ำกว่างบ"} ฿{fmt(Math.abs(live.costVariance))}</p>
              )}
            </div>
            <div><p className="text-[11px] text-gray-500">VAT เคลมคืน</p><p className="text-base font-bold text-gray-700">฿{fmt(live.totalVat)}</p></div>
            <div><p className="text-[11px] text-gray-500">หัก ณ ที่จ่าย</p><p className="text-base font-bold text-red-600">฿{fmt(live.totalWht)}</p></div>
            <div><p className="text-[11px] text-gray-500">กำไร</p><p className={`text-base font-bold ${live.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>฿{fmt(live.profit)}</p></div>
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>ยกเลิก</Button>
          <Button onClick={save} disabled={saving} className="bg-[#3b6ef5] hover:bg-[#3260e0] text-white">
            {saving ? "กำลังบันทึก..." : editing ? "บันทึก" : "สร้างโปรเจค"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog (read view — Option A) ──
function ProjectDetailDialog({ project, onClose, onEdit, onDelete }) {
  const calc = useMemo(() => (project ? calcProject(project) : null), [project]);
  if (!project || !calc) return null;
  const st = STATUS[project.status] || STATUS.planning;
  const names = getAssignees(project);

  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="sr-only">รายละเอียดโปรเจค</DialogTitle>
        </DialogHeader>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 -mt-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.color }}>{st.label}</span>
              <h2 className="text-lg font-bold text-gray-900 truncate">{project.name}</h2>
            </div>
            <p className="text-xs text-gray-500 flex items-center gap-1.5">
              <Calendar size={12} /> {dateRangeLabel(project.start_date, project.end_date)}
            </p>
            {project.description && <p className="text-sm text-gray-600 mt-2">{project.description}</p>}
          </div>
          {names.length > 0 && <AvatarStack names={names} />}
        </div>

        {/* Income installments */}
        {calc.incomes.length > 0 && (
          <div className="rounded-xl border border-gray-200 overflow-hidden mt-3">
            <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-gray-500 uppercase">รายรับ (งวด)</span>
              <span className="text-[11px] text-gray-500">รับแล้ว <b className="text-emerald-600">฿{fmt(calc.received)}</b> · ค้าง <b className={calc.outstanding > 0 ? "text-amber-600" : "text-gray-400"}>฿{fmt(calc.outstanding)}</b></span>
            </div>
            <div className="divide-y divide-gray-100">
              {calc.incomes.map((inc, i) => (
                <div key={inc.id || i} className="flex items-center gap-3 px-3 py-2">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${inc.paid ? "bg-emerald-500" : "bg-amber-400"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 font-medium truncate">{inc.label || <span className="text-gray-400">(งวด)</span>}</p>
                    {inc.dueDate && <p className="text-[11px] text-gray-400">กำหนด {(() => { try { return format(parseISO(inc.dueDate), "d MMM yyyy"); } catch { return inc.dueDate; } })()}</p>}
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${inc.paid ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{inc.paid ? "รับแล้ว" : "ค้างรับ"}</span>
                  <span className="text-sm font-semibold text-gray-900 w-24 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>฿{fmt(inc.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Line items — estimate vs actual */}
        <div className="rounded-xl border border-gray-200 overflow-x-auto mt-3">
          <table className="w-full text-sm min-w-[480px]" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-[11px] uppercase">
                <th className="text-left font-semibold px-3 py-2">รายการ (ต้นทุน)</th>
                <th className="text-right font-semibold px-3 py-2">ประมาณ</th>
                <th className="text-right font-semibold px-3 py-2">จ่ายจริง</th>
                <th className="text-right font-semibold px-3 py-2">ส่วนต่าง</th>
              </tr>
            </thead>
            <tbody className="text-gray-600" style={{ fontVariantNumeric: "tabular-nums" }}>
              {calc.items.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-gray-400 py-6">ยังไม่มีรายการค่าใช้จ่าย</td></tr>
              ) : calc.items.map((it) => (
                <tr key={it.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-gray-900 font-medium">
                    {it.description || <span className="text-gray-400">(ไม่ระบุ)</span>}
                    {it.whtRate > 0 && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">WHT {it.whtRate}%</span>}
                    {!it.vat && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">ไม่มี VAT</span>}
                  </td>
                  <td className="px-3 py-2 text-right">{fmt(it.base)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900">{it.actual > 0 ? fmt(it.actual) : <span className="text-gray-300">ยังไม่จ่าย</span>}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${it.variance > 0 ? "text-red-500" : it.variance < 0 ? "text-emerald-600" : "text-gray-300"}`}>
                    {it.actual > 0 ? `${it.variance > 0 ? "+" : ""}${fmt(it.variance)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
            {calc.items.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold text-gray-900" style={{ fontVariantNumeric: "tabular-nums" }}>
                  <td className="px-3 py-2">รวม</td>
                  <td className="px-3 py-2 text-right">{fmt(calc.totalBase)}</td>
                  <td className="px-3 py-2 text-right">{calc.hasActual ? fmt(calc.totalActual) : <span className="text-gray-300">—</span>}</td>
                  <td className={`px-3 py-2 text-right ${calc.costVariance > 0 ? "text-red-500" : calc.costVariance < 0 ? "text-emerald-600" : "text-gray-300"}`}>
                    {calc.hasActual ? `${calc.costVariance > 0 ? "+" : ""}${fmt(calc.costVariance)}` : "—"}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Summary (horizontal — Option A) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-1">
          <div className="rounded-xl bg-gray-50 px-3 py-2.5">
            <p className="text-[11px] text-gray-500">ต้นทุน {calc.hasActual ? "(จ่ายจริง)" : "(ประมาณ)"}</p>
            <p className="text-lg font-extrabold text-gray-900">฿{fmt(calc.cost)}</p>
            {calc.hasActual && (
              <p className="text-[10px] mt-0.5">
                <span className="text-gray-400">ประมาณ ฿{fmt(calc.estCost)}</span>
                {calc.costVariance !== 0 && <span className={calc.costVariance > 0 ? "text-red-500" : "text-emerald-600"}> · {calc.costVariance > 0 ? "เกินงบ" : "ต่ำกว่างบ"} ฿{fmt(Math.abs(calc.costVariance))}</span>}
              </p>
            )}
          </div>
          <div className="rounded-xl px-3 py-2.5" style={{ background: "#e6edfd" }}>
            <p className="text-[11px] text-[#3b6ef5]">รายได้ (ราคาขาย)</p>
            <p className="text-lg font-extrabold text-[#3b6ef5]">฿{fmt(calc.revenue)}</p>
            {calc.revenue > 0 && (
              <p className="text-[10px] mt-0.5">
                <span className="text-emerald-600">รับแล้ว ฿{fmt(calc.received)}</span>
                {calc.outstanding > 0 && <span className="text-amber-600"> · ค้าง ฿{fmt(calc.outstanding)}</span>}
              </p>
            )}
          </div>
          <div className="rounded-xl px-3 py-2.5" style={{ background: calc.profit >= 0 ? "#dcfce7" : "#fee2e2" }}>
            <p className="text-[11px]" style={{ color: calc.profit >= 0 ? "#16a34a" : "#dc2626" }}>
              กำไร · {calc.margin.toFixed(1)}%
            </p>
            <p className="text-lg font-extrabold" style={{ color: calc.profit >= 0 ? "#16a34a" : "#dc2626" }}>฿{fmt(calc.profit)}</p>
          </div>
          <div className="rounded-xl bg-gray-50 px-3 py-2.5">
            <p className="text-[11px] text-gray-500">VAT คืน · WHT นำส่ง</p>
            <p className="text-sm font-bold text-gray-700 mt-1">฿{fmt(calc.totalVat)} · ฿{fmt(calc.totalWht)}</p>
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center gap-2 pt-3">
          <button onClick={() => onDelete(project)} className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1.5">
            <Trash2 size={15} /> ลบโปรเจค
          </button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => openProjectPrint(project, "quotation")} className="text-gray-700">
              <FileText size={15} className="mr-1.5" /> ใบเสนอราคา
            </Button>
            <Button variant="outline" onClick={() => openProjectPrint(project, "cost")} className="text-gray-700">
              <FileText size={15} className="mr-1.5" /> สรุปต้นทุน
            </Button>
            <Button onClick={() => onEdit(project)} className="bg-[#3b6ef5] hover:bg-[#3260e0] text-white">
              <Pencil size={15} className="mr-1.5" /> แก้ไข
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Project card ──
function ProjectCard({ project, onOpen }) {
  const calc = calcProject(project);
  const st = STATUS[project.status] || STATUS.planning;
  const names = getAssignees(project);
  return (
    <button onClick={() => onOpen(project)}
      className="text-left w-full rounded-2xl bg-white border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all"
      style={{ borderLeft: `3px solid ${st.color}` }}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.color }}>{st.label}</span>
          <h3 className="text-[15px] font-bold text-gray-900 mt-1.5 truncate">{project.name}</h3>
          <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><Calendar size={11} /> {dateRangeLabel(project.start_date, project.end_date)}</p>
        </div>
        {names.length > 0 && <AvatarStack names={names} size={24} />}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-100">
        <div><p className="text-[10px] text-gray-400">ต้นทุน</p><p className="text-sm font-bold text-gray-900">฿{fmt(calc.cost)}</p></div>
        <div><p className="text-[10px] text-gray-400">รายได้</p><p className="text-sm font-bold text-[#3b6ef5]">฿{fmt(calc.revenue)}</p></div>
        <div>
          <p className="text-[10px] text-gray-400">กำไร {calc.revenue > 0 ? `· ${calc.margin.toFixed(0)}%` : ""}</p>
          <p className={`text-sm font-bold ${calc.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>฿{fmt(calc.profit)}</p>
        </div>
      </div>
      {calc.revenue > 0 && (
        <div className="mt-2.5">
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, (calc.received / calc.revenue) * 100)}%` }} />
          </div>
          <p className="text-[10px] mt-1 flex items-center justify-between">
            <span className="text-emerald-600 font-medium">รับแล้ว ฿{fmt(calc.received)}</span>
            {calc.outstanding > 0
              ? <span className="text-amber-600 font-medium">ค้าง ฿{fmt(calc.outstanding)}</span>
              : <span className="text-gray-400">รับครบ</span>}
          </p>
        </div>
      )}
    </button>
  );
}

// ── Timeline / Gantt view (monthly) ──
function ProjectTimeline({ projects, month, setMonth, onOpen }) {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const DAY_W = 34, NAME_W = 150, ROW_H = 40;

  const rows = projects
    .filter((p) => p.start_date || p.end_date)
    .map((p) => ({ p, s: parseISO(p.start_date || p.end_date), e: parseISO(p.end_date || p.start_date) }))
    .filter(({ s, e }) => s <= monthEnd && e >= monthStart)
    .sort((a, b) => a.s - b.s);
  const noDate = projects.filter((p) => !p.start_date && !p.end_date).length;

  const today = new Date();
  const todayLeft = isSameMonth(today, month) ? differenceInCalendarDays(today, monthStart) * DAY_W : -1;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <GanttChartSquare size={16} className="text-[#3b6ef5]" />
        <button onClick={() => setMonth((m) => startOfMonth(addMonths(m, -1)))} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700"><ChevronLeft size={15} /></button>
        <span className="text-sm font-semibold text-gray-800 min-w-[130px] text-center">{format(month, "MMMM yyyy")}</span>
        <button onClick={() => setMonth((m) => startOfMonth(addMonths(m, 1)))} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700"><ChevronRight size={15} /></button>
        {!isSameMonth(today, month) && <button onClick={() => setMonth(startOfMonth(today))} className="ml-1 text-xs px-2.5 py-1 rounded-md border border-blue-200 text-[#3b6ef5] hover:bg-blue-50">วันนี้</button>}
        <span className="ml-auto text-xs text-gray-400">{rows.length} โปรเจค</span>
      </div>

      {rows.length === 0 ? (
        <div className="py-14 text-center text-gray-400 text-sm">ไม่มีโปรเจคในเดือนนี้</div>
      ) : (
        <div className="overflow-x-auto">
          <div style={{ minWidth: NAME_W + days.length * DAY_W }}>
            <div className="flex border-b border-gray-100 bg-gray-50">
              <div className="flex-shrink-0 border-r border-gray-100 px-3 py-2 text-[11px] font-medium text-gray-400 uppercase sticky left-0 bg-gray-50 z-10" style={{ width: NAME_W, minWidth: NAME_W }}>โปรเจค</div>
              <div className="flex">
                {days.map((day, i) => (
                  <div key={i} style={{ width: DAY_W, minWidth: DAY_W }} className={`flex-shrink-0 text-center py-2 border-r border-gray-100 last:border-r-0 ${isToday(day) ? "bg-red-50 text-red-500 font-semibold" : (day.getDay() === 0 || day.getDay() === 6) ? "text-gray-300 bg-gray-50/80" : "text-gray-400"}`}>
                    <div className="text-[11px] leading-tight font-medium">{format(day, "d")}</div>
                    <div className="text-[9px] leading-tight opacity-60">{format(day, "EEE")}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              {todayLeft >= 0 && <div className="absolute top-0 bottom-0 w-px bg-red-300 z-[6] pointer-events-none" style={{ left: NAME_W + todayLeft + DAY_W / 2 }} />}
              {rows.map(({ p, s, e }) => {
                const st = STATUS[p.status] || STATUS.planning;
                const cs = s < monthStart ? monthStart : s;
                const ce = e > monthEnd ? monthEnd : e;
                const left = differenceInCalendarDays(cs, monthStart) * DAY_W;
                const width = (differenceInCalendarDays(ce, cs) + 1) * DAY_W;
                const calc = calcProject(p);
                return (
                  <div key={p.id} className="flex border-b border-gray-50 last:border-b-0 hover:bg-gray-50/40" style={{ height: ROW_H }}>
                    <div className="flex-shrink-0 border-r border-gray-100 px-3 sticky left-0 bg-white z-[5] flex items-center gap-2" style={{ width: NAME_W, minWidth: NAME_W }}>
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: st.color }} />
                      <span className="text-[12px] font-medium text-gray-800 truncate">{p.name}</span>
                    </div>
                    <div className="relative flex-shrink-0" style={{ width: days.length * DAY_W, height: ROW_H }}>
                      <button onClick={() => onOpen(p)} title={`${p.name} · ${st.label} · ${dateRangeLabel(p.start_date, p.end_date)}`}
                        className="absolute top-1/2 -translate-y-1/2 h-6 rounded-md flex items-center px-2 text-[11px] font-semibold text-white truncate hover:brightness-95 transition-all"
                        style={{ left: left + 2, width: Math.max(width - 4, 22), background: st.color }}>
                        {calc.revenue > 0 ? `฿${fmt(calc.profit)}` : p.name}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {noDate > 0 && <div className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100">+ {noDate} โปรเจคยังไม่กำหนดวันที่ (ดูในมุมมองการ์ด)</div>}
    </div>
  );
}

// ── Page ──
export default function ProjectsPage() {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const [projects, setProjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("cards"); // cards | timeline
  const [ganttMonth, setGanttMonth] = useState(startOfMonth(new Date()));

  useEffect(() => {
    const unsubP = projectsApi.subscribe((data) => { setProjects(data); setLoading(false); }, () => setLoading(false));
    const unsubS = staffApi.subscribe((data) => setStaff(data.filter((s) => s.status !== "inactive")));
    return () => { unsubP(); unsubS(); };
  }, []);

  const filtered = useMemo(() => {
    const list = filter === "all" ? projects : projects.filter((p) => (p.status || "planning") === filter);
    return [...list].sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
  }, [projects, filter]);

  const totals = useMemo(() => {
    const active = projects.filter((p) => p.status !== "cancelled");
    return active.reduce((acc, p) => {
      const c = calcProject(p);
      acc.cost += c.cost; acc.revenue += c.revenue; acc.profit += c.profit;
      return acc;
    }, { cost: 0, revenue: 0, profit: 0 });
  }, [projects]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p) => { setDetail(null); setEditing(p); setFormOpen(true); };
  const handleDelete = async (p) => {
    if (!window.confirm(`ลบโปรเจค "${p.name}" ?`)) return;
    try { await projectsApi.delete(p.id); setDetail(null); toast.success("ลบแล้ว"); }
    catch { toast.error("ลบไม่สำเร็จ"); }
  };

  const counts = useMemo(() => {
    const c = { all: projects.length };
    STATUS_ORDER.forEach((k) => { c[k] = projects.filter((p) => (p.status || "planning") === k).length; });
    return c;
  }, [projects]);

  return (
    <div className="page-container" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <FolderKanban size={28} className="text-[#3b6ef5]" /> Projects
          </h1>
          <p className="text-gray-500 mt-1">คำนวณต้นทุน กำไร และภาษีแต่ละโปรเจค</p>
        </div>
        <Button onClick={openNew} className="bg-[#3b6ef5] hover:bg-[#3260e0] text-white self-start">
          <Plus size={16} className="mr-1.5" /> สร้างโปรเจค
        </Button>
      </div>

      {/* Portfolio totals */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl bg-white border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0"><Wallet size={17} className="text-gray-500" /></div>
          <div><p className="text-[11px] text-gray-400">ต้นทุนรวม</p><p className="text-lg sm:text-xl font-extrabold text-gray-900">฿{fmt(totals.cost)}</p></div>
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#e6edfd" }}><Receipt size={17} className="text-[#3b6ef5]" /></div>
          <div><p className="text-[11px] text-gray-400">รายได้รวม</p><p className="text-lg sm:text-xl font-extrabold text-[#3b6ef5]">฿{fmt(totals.revenue)}</p></div>
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: totals.profit >= 0 ? "#dcfce7" : "#fee2e2" }}><TrendingUp size={17} style={{ color: totals.profit >= 0 ? "#16a34a" : "#dc2626" }} /></div>
          <div><p className="text-[11px] text-gray-400">กำไรรวม</p><p className="text-lg sm:text-xl font-extrabold" style={{ color: totals.profit >= 0 ? "#16a34a" : "#dc2626" }}>฿{fmt(totals.profit)}</p></div>
        </div>
      </div>

      {/* Status filter + view toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[{ k: "all", label: "ทั้งหมด" }, ...STATUS_ORDER.map((k) => ({ k, label: STATUS[k].label }))].map(({ k, label }) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === k ? "bg-[#3b6ef5] text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"}`}>
            {label} <span className={filter === k ? "text-white/70" : "text-gray-400"}>{counts[k] ?? 0}</span>
          </button>
        ))}
        <div className="ml-auto flex items-center rounded-lg border border-gray-200 bg-white p-0.5">
          <button onClick={() => setView("cards")} title="การ์ด"
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${view === "cards" ? "bg-[#3b6ef5] text-white" : "text-gray-500 hover:text-gray-800"}`}>
            <LayoutGrid size={14} /> การ์ด
          </button>
          <button onClick={() => setView("timeline")} title="ไทม์ไลน์"
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${view === "timeline" ? "bg-[#3b6ef5] text-white" : "text-gray-500 hover:text-gray-800"}`}>
            <GanttChartSquare size={14} /> ไทม์ไลน์
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-[#3b6ef5] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : view === "timeline" ? (
        <ProjectTimeline projects={filtered} month={ganttMonth} setMonth={setGanttMonth} onOpen={setDetail} />
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl bg-white border border-gray-200 py-16 flex flex-col items-center gap-3">
          <FolderKanban size={40} className="text-gray-300" />
          <p className="text-gray-400">{filter === "all" ? "ยังไม่มีโปรเจค — เริ่มสร้างอันแรกเลย" : "ไม่มีโปรเจคในสถานะนี้"}</p>
          {filter === "all" && <Button onClick={openNew} className="bg-[#3b6ef5] hover:bg-[#3260e0] text-white"><Plus size={16} className="mr-1.5" /> สร้างโปรเจค</Button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => <ProjectCard key={p.id} project={p} onOpen={setDetail} />)}
        </div>
      )}

      <ProjectFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} staff={staff} onSaved={() => {}} />
      <ProjectDetailDialog project={detail} onClose={() => setDetail(null)} onEdit={openEdit} onDelete={handleDelete} />
    </div>
  );
}
